/**
 * lobbyLogic.ts — pure, framework-free lobby decisions. Kept separate from the
 * screen so they're unit-testable without a DOM (node-env Vitest) and mirror the
 * backend authority exactly (never trust the UI for authorization).
 */
import type { LobbyState, SessionPlayer } from '../../types/game';
import {
  resolveEffectiveCosmetics,
  VIEWER_TABLE_SKINS,
  VIEWER_TILE_SKINS,
  type EffectiveCosmetics,
  type AppearanceMode,
  type ViewerTableSkin,
  type ViewerTileSkin,
} from '../../config/viewerCosmetics';

export interface LobbyControls {
  /** Public/Private visibility control (host only). */
  showVisibilityControl: boolean;
  /** Add Bot / Fill Seats controls (host only). */
  showBotControls: boolean;
  /** Invite Friend control (host only). */
  showInvite: boolean;
  /** The host's Play button. */
  showPlay: boolean;
  /** The guest's Get Ready button. */
  showGetReady: boolean;
  /** Host is locked to Host Skin (cannot switch to My Skin). */
  appearanceLockedToHost: boolean;
}

/** Which controls a viewer sees — derived purely from host vs guest. */
export function deriveLobbyControls(isHost: boolean): LobbyControls {
  return {
    showVisibilityControl: isHost,
    showBotControls: isHost,
    showInvite: isHost,
    showPlay: isHost,
    showGetReady: !isHost,
    appearanceLockedToHost: isHost,
  };
}

export function findMe(lobby: LobbyState | null, myUserId: number | null): SessionPlayer | undefined {
  if (!lobby || myUserId == null) return undefined;
  return lobby.players.find((p) => p.user_id === myUserId);
}

export function isHostViewer(lobby: LobbyState | null, myUserId: number | null): boolean {
  return findMe(lobby, myUserId)?.is_host ?? false;
}

/**
 * Host Play eligibility — mirrors the backend `Session#startable?` gate so the
 * UI reflects (never defines) authority:
 *   • still in the waiting lobby
 *   • every seat filled (human or bot)
 *   • a host assigned + valid host cosmetics
 *   • every non-host human is Ready
 */
export function computePlayEnabled(lobby: LobbyState | null): boolean {
  if (!lobby) return false;
  if (lobby.status !== 'waiting') return false;
  if (lobby.player_count < lobby.max_players) return false;
  if (lobby.host_user_id == null) return false;
  if (!VIEWER_TABLE_SKINS.includes(lobby.host_table_skin as ViewerTableSkin)) return false;
  if (!VIEWER_TILE_SKINS.includes(lobby.host_tile_skin as ViewerTileSkin)) return false;
  const nonHostHumans = lobby.players.filter((p) => !p.is_bot && !p.is_host);
  return nonHostHumans.every((p) => p.ready);
}

/** Resolve the effective table+tile skin a given viewer should preview/render. */
export function effectiveCosmeticsForViewer(
  lobby: LobbyState | null,
  me: SessionPlayer | undefined
): EffectiveCosmetics {
  return resolveEffectiveCosmetics({
    appearanceMode: (me?.appearance_mode ?? 'host') as AppearanceMode,
    isHost: me?.is_host ?? false,
    hostTableSkin: lobby?.host_table_skin,
    hostTileSkin: lobby?.host_tile_skin,
    selectedTableSkin: me?.selected_table_skin,
    selectedTileSkin: me?.selected_tile_skin,
  });
}

/** True when a seat's player should show the green Ready radiance. Only human
 *  guests earn it: the host confirms via Play (no glow), and bots are auto-ready
 *  but don't display a standing glow (the radiance signals a human's confirmation). */
export function showsReadyGlow(player: SessionPlayer | undefined): boolean {
  if (!player) return false;
  return !player.is_host && !player.is_bot && player.ready;
}
